// Action constants
export const ACTIONS = {
  ADD: 'add',
  EDIT: 'edit', 
  REMOVE: 'remove',
} as const;

export type Action = typeof ACTIONS[keyof typeof ACTIONS] | null;

// Preview mode constants
export const PREVIEW_MODES = {
  EDIT: 'edit',
  PREVIEW: 'preview',
  LIVE: 'live',
} as const;

export type PreviewMode = typeof PREVIEW_MODES[keyof typeof PREVIEW_MODES];

// Job form mode constants
export const JOB_MODES = {
  CREATE: 'create',
  EDIT: 'edit',
} as const;

export type JobMode = typeof JOB_MODES[keyof typeof JOB_MODES];

export const COUNTRY_CODES = [
  { code: '1', name: 'United States/Canada' },
  { code: '44', name: 'United Kingdom' },
  { code: '91', name: 'India' },
  { code: '86', name: 'China' },
  { code: '7', name: 'Russia' },
  { code: '55', name: 'Brazil' },
  { code: '62', name: 'Indonesia' },
  { code: '81', name: 'Japan' },
  { code: '49', name: 'Germany' },
  { code: '90', name: 'Turkey' },
  { code: '33', name: 'France' },
  { code: '39', name: 'Italy' },
  { code: '34', name: 'Spain' },
  { code: '82', name: 'South Korea' },
  { code: '52', name: 'Mexico' },
  { code: '61', name: 'Australia' },
  { code: '31', name: 'Netherlands' },
  { code: '966', name: 'Saudi Arabia' },
  { code: '971', name: 'UAE' },
  { code: '974', name: 'Qatar' },
  { code: '965', name: 'Kuwait' },
  { code: '968', name: 'Oman' },
  { code: '973', name: 'Bahrain' },
  { code: '98', name: 'Iran' },
  { code: '964', name: 'Iraq' },
  { code: '972', name: 'Israel' },
  { code: '20', name: 'Egypt' },
  { code: '962', name: 'Jordan' },
  { code: '961', name: 'Lebanon' },
  { code: '41', name: 'Switzerland' },
  { code: '46', name: 'Sweden' },
  { code: '47', name: 'Norway' },
  { code: '32', name: 'Belgium' },
  { code: '65', name: 'Singapore' },
  { code: '66', name: 'Thailand' },
  { code: '63', name: 'Philippines' },
  { code: '60', name: 'Malaysia' },
  { code: '27', name: 'South Africa' },
  { code: '54', name: 'Argentina' },
  { code: '48', name: 'Poland' },
  // Add more country codes as needed
];

export const JOB_TITLES = [
  'Software Engineer',
  'Product Manager',
  'HR Manager',
  'Marketing Manager',
  'Sales Manager',
  'Operations Manager',
  'Finance Manager',
  'Business Analyst',
  'Project Manager',
  'Designer',
  'Content Writer',
  'Other',
];

export const COMPANY_DOMAINS = [
  'Technology',
  'Healthcare',
  'Finance',
  'Education',
  'Manufacturing',
  'Retail',
  'Media & Entertainment',
  'Real Estate',
  'Transportation',
  'Energy',
  'Other',
];

// Notification channel enum matching backend @jedi/core CHANNEL enum
export enum CHANNEL {
  EMAIL = 'EMAIL',
  WHATSAPP = 'WHATSAPP',
  CALL = 'CALL',
}

// Interview type constants matching backend @jedi/core InterviewType enum
export const INTERVIEW_TYPE = {
  BACKGROUND: 'background',
  SKILL: 'skill',
  DESIGNATION: 'designation',
  SCREENING: 'screening',
  CUSTOM: 'custom',
  UNIFIED: 'unified',
} as const;

export type InterviewType = typeof INTERVIEW_TYPE[keyof typeof INTERVIEW_TYPE];

export const CURRENCIES = [
  // North America
  { value: 'USD', label: '$', locale: 'en-US' },
  { value: 'CAD', label: 'C$', locale: 'en-CA' },
  
  // Europe
  { value: 'EUR', label: '€', locale: 'de-DE' },
  { value: 'GBP', label: '£', locale: 'en-GB' },
  { value: 'CHF', label: 'CHF', locale: 'de-CH' },
  { value: 'SEK', label: 'kr', locale: 'sv-SE' },
  { value: 'NOK', label: 'kr', locale: 'nb-NO' },
  { value: 'PLN', label: 'zł', locale: 'pl-PL' },
  
  // Asia
  { value: 'INR', label: '₹', locale: 'en-IN' },
  { value: 'CNY', label: '¥', locale: 'zh-CN' },
  { value: 'JPY', label: '¥', locale: 'ja-JP' },
  { value: 'KRW', label: '₩', locale: 'ko-KR' },
  { value: 'SGD', label: 'S$', locale: 'en-SG' },
  { value: 'THB', label: '฿', locale: 'th-TH' },
  { value: 'PHP', label: '₱', locale: 'en-PH' },
  { value: 'MYR', label: 'RM', locale: 'ms-MY' },
  { value: 'IDR', label: 'Rp', locale: 'id-ID' },
  
  // Middle East
  { value: 'SAR', label: 'ر.س', locale: 'ar-SA' },
  { value: 'AED', label: 'د.إ', locale: 'ar-AE' },
  { value: 'QAR', label: 'ر.ق', locale: 'ar-QA' },
  { value: 'KWD', label: 'د.ك', locale: 'ar-KW' },
  { value: 'OMR', label: 'ر.ع.', locale: 'ar-OM' },
  { value: 'BHD', label: 'د.ب', locale: 'ar-BH' },
  { value: 'IRR', label: '﷼', locale: 'fa-IR' },
  { value: 'IQD', label: 'ع.د', locale: 'ar-IQ' },
  { value: 'ILS', label: '₪', locale: 'he-IL' },
  { value: 'EGP', label: 'ج.م', locale: 'ar-EG' },
  { value: 'JOD', label: 'د.ا', locale: 'ar-JO' },
  { value: 'LBP', label: 'ل.ل', locale: 'ar-LB' },
  
  // Other regions
  { value: 'RUB', label: '₽', locale: 'ru-RU' },
  { value: 'BRL', label: 'R$', locale: 'pt-BR' },
  { value: 'MXN', label: '$', locale: 'es-MX' },
  { value: 'AUD', label: 'A$', locale: 'en-AU' },
  { value: 'ZAR', label: 'R', locale: 'en-ZA' },
  { value: 'ARS', label: '$', locale: 'es-AR' },
  { value: 'TRY', label: '₺', locale: 'tr-TR' },
] as const;
